import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { toolsHubPath } from '@/lib/toolRoutes';
import {
  ChartBar,
  ArrowRight,
  CheckCircle,
  Lightning,
  Target,
  Globe,
  TrendUp,
  ShieldCheck,
  Sparkle,
  UserCircle,
  ClipboardText,
  PresentationChart,
  Storefront,
  CurrencyCircleDollar,
} from '@phosphor-icons/react';
import Logo from '@/components/Logo';
import useEventMode from '@/hooks/useEventMode';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const { enabled: eventMode } = useEventMode();

  // SEO metadata — update <title> and <meta> tags per language
  useEffect(() => {
    document.title = t('landing.seo.title');
    const setMeta = (key, keyValue, value) => {
      // key is 'name' or 'property'; keyValue is e.g. 'og:title'
      let el = document.head.querySelector(`meta[${key}="${keyValue}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(key, keyValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };
    setMeta('name', 'description', t('landing.seo.description'));
    setMeta('property', 'og:title', t('landing.seo.title'));
    setMeta('property', 'og:description', t('landing.seo.description'));
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:locale', language === 'tr' ? 'tr_TR' : 'en_US');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', t('landing.seo.title'));
    setMeta('name', 'twitter:description', t('landing.seo.description'));
    document.documentElement.setAttribute('lang', language);

    // FAQPage Schema.org JSON-LD — boosts Google rich results + AI Overview
    document.head.querySelectorAll('script[data-ld="faqpage"]').forEach((n) => n.remove());
    const faqEntries = [1, 2, 3, 4, 5, 6].map((n) => ({
      "@type": "Question",
      "name": t(`landing.faq.${n}.q`),
      "acceptedAnswer": { "@type": "Answer", "text": t(`landing.faq.${n}.a`) },
    }));
    const faqLd = document.createElement('script');
    faqLd.type = 'application/ld+json';
    faqLd.setAttribute('data-ld', 'faqpage');
    faqLd.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqEntries,
    });
    document.head.appendChild(faqLd);

    // Organization Schema.org JSON-LD — sitelinks + brand entity
    document.head.querySelectorAll('script[data-ld="organization"]').forEach((n) => n.remove());
    const orgLd = document.createElement('script');
    orgLd.type = 'application/ld+json';
    orgLd.setAttribute('data-ld', 'organization');
    orgLd.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "GrowthRIQ",
      "url": window.location.origin,
      "logo": `${window.location.origin}/favicon.ico`,
      "description": t('landing.seo.description'),
      "sameAs": [],
    });
    document.head.appendChild(orgLd);
  }, [t, language]);

  const features = [
    { icon: <Globe size={32} weight="bold" />, title: t('landing.feature.1.title'), description: t('landing.feature.1.desc') },
    { icon: <Target size={32} weight="bold" />, title: t('landing.feature.2.title'), description: t('landing.feature.2.desc') },
    { icon: <ChartBar size={32} weight="bold" />, title: t('landing.feature.3.title'), description: t('landing.feature.3.desc') },
    { icon: <CurrencyCircleDollar size={32} weight="bold" />, title: t('landing.feature.4.title'), description: t('landing.feature.4.desc') },
    { icon: <Storefront size={32} weight="bold" />, title: t('landing.feature.5.title'), description: t('landing.feature.5.desc') },
    { icon: <PresentationChart size={32} weight="bold" />, title: t('landing.feature.6.title'), description: t('landing.feature.6.desc') },
  ];

  const benefits = [
    t('landing.benefit.1'),
    t('landing.benefit.2'),
    t('landing.benefit.3'),
    t('landing.benefit.4'),
    t('landing.benefit.5'),
  ];

  const stats = [
    { value: t('landing.stats.1.value'), label: t('landing.stats.1.label') },
    { value: t('landing.stats.2.value'), label: t('landing.stats.2.label') },
    { value: t('landing.stats.3.value'), label: t('landing.stats.3.label') },
    { value: t('landing.stats.4.value'), label: t('landing.stats.4.label') },
  ];

  const howSteps = [
    { icon: <UserCircle size={28} weight="bold" />, title: t('landing.how.1.title'), desc: t('landing.how.1.desc') },
    { icon: <ClipboardText size={28} weight="bold" />, title: t('landing.how.2.title'), desc: t('landing.how.2.desc') },
    { icon: <PresentationChart size={28} weight="bold" />, title: t('landing.how.3.title'), desc: t('landing.how.3.desc') },
  ];

  const tiers = [
    { name: t('landing.pricing.free.name'), price: t('landing.pricing.free.price'), desc: t('landing.pricing.free.desc'), highlight: false },
    { name: t('landing.pricing.basic.name'), price: t('landing.pricing.basic.price'), desc: t('landing.pricing.basic.desc'), highlight: true },
    { name: t('landing.pricing.advanced.name'), price: t('landing.pricing.advanced.price'), desc: t('landing.pricing.advanced.desc'), highlight: false },
  ];

  const faqs = [1, 2, 3, 4, 5, 6].map((n) => ({
    q: t(`landing.faq.${n}.q`),
    a: t(`landing.faq.${n}.a`),
  }));

  const trustBadges = [
    t('landing.hero.trust.1'),
    t('landing.hero.trust.2'),
    t('landing.hero.trust.3'),
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-[#E4E4E7] sticky top-0 bg-white/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo variant="light" size="md" />
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to={language === 'en' ? '/en/blog' : '/blog'} className="text-[#52525B] hover:text-[#09090B] font-medium transition-colors hidden sm:inline" data-testid="nav-blog-link">
              {t('nav.blog')}
            </Link>
            <Link to={toolsHubPath(language)} className="text-[#52525B] hover:text-[#09090B] font-medium transition-colors hidden sm:inline" data-testid="nav-tools-link">
              {t('nav.tools')}
            </Link>
            <Link to="/e-ihracat-danismanlik" className="text-[#52525B] hover:text-[#09090B] font-medium transition-colors hidden md:inline" data-testid="nav-consulting-link">
              E-İhracat Danışmanlığı
            </Link>
            <Link to="/pricing" className="text-[#52525B] hover:text-[#09090B] font-medium transition-colors hidden sm:inline" data-testid="nav-pricing-link">
              {t('nav.pricing')}
            </Link>
            {isAuthenticated ? (
              <Link to="/dashboard" className="btn-primary" data-testid="nav-dashboard-link">{t('nav.dashboard')}</Link>
            ) : (
              <>
                <Link to="/login" className="text-[#52525B] hover:text-[#09090B] font-medium transition-colors hidden sm:inline" data-testid="nav-login-link">
                  {t('nav.login')}
                </Link>
                <Link to="/register" className="btn-primary" data-testid="nav-register-link">{t('nav.register')}</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-24 px-6 border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <p className="label-text text-[#3CD9B8] mb-4 font-semibold tracking-wider" data-testid="hero-label">
              {t('landing.hero.label')}
            </p>
            <h1 className="heading-1 text-[#0F1B36] mb-3" data-testid="hero-title">
              {t('landing.hero.title')}
            </h1>
            <p className="text-2xl text-[#52525B] mb-6 font-medium" data-testid="hero-subtitle">
              {t('landing.hero.subtitle')}
            </p>
            <p className="text-xl text-[#52525B] mb-8 leading-relaxed" data-testid="hero-description">
              {t('landing.hero.description')}
            </p>
            <div className="flex flex-wrap gap-4 mb-6">
              <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-primary">
                {t('landing.hero.cta.primary')}
                <ArrowRight size={20} weight="bold" />
              </Link>
              <Link to="/demo" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-cta-demo">
                {t('landing.hero.cta.demo')}
              </Link>
              <Link to="/pricing" className="text-sm font-semibold text-[#52525B] hover:text-[#0F1B36] inline-flex items-center gap-1 self-center" data-testid="hero-cta-secondary">
                {t('landing.hero.cta.secondary')} →
              </Link>
            </div>
            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#52525B]" data-testid="hero-trust-badges">
              {trustBadges.map((b, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-[#16A34A]" />
                  {b}
                </span>
              ))}
            </div>
          </div>
          <div className="md:col-span-5">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/44d57446-5912-4aae-ac11-a44e3c167ac5/images/a25799d759c1b61d92144516fea3803af7adb198137db6e51dfcd2f3542ba18d.png"
              alt={t('landing.seo.title')}
              className="w-full border border-[#E4E4E7]"
              data-testid="hero-image"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Stats Bar — Social Proof */}
      <section className="py-12 px-6 border-b border-[#E4E4E7] bg-white" data-testid="stats-bar">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <div key={i} className="text-center" data-testid={`stat-${i}`}>
              <div className="text-4xl md:text-5xl font-bold text-[#0F1B36] mb-2">{s.value}</div>
              <div className="text-sm text-[#52525B]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works — 3 steps */}
      <section className="py-24 px-6 border-b border-[#E4E4E7]" data-testid="how-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-text text-[#0F1B36] mb-4">{t('landing.how.label')}</p>
            <h2 className="heading-2 text-[#09090B]">{t('landing.how.title')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {howSteps.map((step, i) => (
              <div key={i} className="relative p-8 border border-[#E4E4E7] bg-white" data-testid={`how-step-${i + 1}`}>
                <div className="absolute -top-4 left-8 bg-[#0F1B36] text-white w-10 h-10 flex items-center justify-center font-bold text-lg">
                  {i + 1}
                </div>
                <div className="text-[#0F1B36] mb-4 mt-2">{step.icon}</div>
                <h3 className="heading-4 text-[#09090B] mb-2">{step.title}</h3>
                <p className="text-[#52525B]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-text text-[#0F1B36] mb-4">{t('landing.features.label')}</p>
            <h2 className="heading-2 text-[#09090B]">{t('landing.features.title')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card-technical" data-testid={`feature-card-${index}`}>
                <div className="text-[#0F1B36] mb-4">{feature.icon}</div>
                <h3 className="heading-4 text-[#09090B] mb-2">{feature.title}</h3>
                <p className="text-[#52525B]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1748439281934-2803c6a3ee36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxkYXRhJTIwY2hhcnQlMjBhbmFseXRpY3N8ZW58MHx8fHwxNzc1ODEzNjE0fDA&ixlib=rb-4.1.0&q=85"
              alt="Analytics dashboard preview"
              className="w-full border border-[#E4E4E7]"
              data-testid="benefits-image"
              loading="lazy"
            />
          </div>
          <div className="md:col-span-7">
            <p className="label-text text-[#0F1B36] mb-4">{t('landing.benefits.label')}</p>
            <h2 className="heading-2 text-[#09090B] mb-6">{t('landing.benefits.title')}</h2>
            <ul className="space-y-4">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-3" data-testid={`benefit-item-${index}`}>
                  <CheckCircle size={24} weight="fill" className="text-[#16A34A] flex-shrink-0" />
                  <span className="text-[#09090B]">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="py-24 px-6 bg-[#F4F4F5]" data-testid="pricing-teaser-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-text text-[#0F1B36] mb-4">{t('landing.pricing.label')}</p>
            <h2 className="heading-2 text-[#09090B]">{t('landing.pricing.title')}</h2>
            {eventMode && (
              <div className="mt-6 inline-flex items-center gap-3 bg-gradient-to-r from-[#0F1B36] to-[#1E3A8A] text-white px-5 py-3 rounded-lg" data-testid="event-landing-pricing-banner">
                <Sparkle size={18} weight="fill" className="text-[#3CD9B8]" />
                <span className="font-bold text-sm">
                  {language === 'tr'
                    ? '🎉 Etkinlik Özel — Tüm paketler şu anda ÜCRETSİZ'
                    : '🎉 Event Special — All packages currently FREE'}
                </span>
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {tiers.map((tier, i) => (
              <div
                key={i}
                className={`p-8 bg-white border ${tier.highlight ? 'border-[#0F1B36] border-2 relative' : 'border-[#E4E4E7]'}`}
                data-testid={`pricing-tier-${i}`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0F1B36] text-white text-xs font-bold px-3 py-1 flex items-center gap-1">
                    <Sparkle size={12} weight="fill" /> POPULAR
                  </div>
                )}
                <h3 className="heading-4 text-[#09090B] mb-2">{tier.name}</h3>
                {eventMode ? (
                  <div className="mb-4">
                    <div className="text-2xl font-bold text-[#52525B] line-through opacity-60">{tier.price}</div>
                    <div className="text-3xl font-black text-[#16A34A] mt-1">
                      {language === 'tr' ? 'ÜCRETSİZ' : 'FREE'}
                    </div>
                  </div>
                ) : (
                  <div className="text-4xl font-bold text-[#0F1B36] mb-4">{tier.price}</div>
                )}
                <p className="text-[#52525B] text-sm">{tier.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/pricing" className="btn-secondary inline-flex items-center gap-2" data-testid="pricing-see-all">
              {t('landing.pricing.see_all')}
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 border-b border-[#E4E4E7]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="label-text text-[#0F1B36] mb-4">{t('landing.faq.label')}</p>
            <h2 className="heading-2 text-[#09090B]">{t('landing.faq.title')}</h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-semibold text-[#09090B]">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#52525B] leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 bg-[#09090B]">
        <div className="max-w-3xl mx-auto text-center">
          <ShieldCheck size={48} weight="bold" className="text-[#0F1B36] mx-auto mb-6" />
          <h2 className="heading-2 text-white mb-4">{t('landing.cta.title')}</h2>
          <p className="text-xl text-[#A1A1AA] mb-8">{t('landing.cta.description')}</p>
          <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="cta-register">
            {t('landing.cta.button')}
            <ArrowRight size={20} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#E4E4E7] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <Logo variant="dark" size="md" />
              <p className="text-xs text-[#52525B] mt-3 leading-relaxed">{t('landing.footer.tagline')}</p>
            </div>

            <div>
              <p className="font-bold text-[#0F1B36] text-sm mb-3">{t('footer.col.product')}</p>
              <ul className="space-y-2 text-sm text-[#52525B]">
                <li><Link to={toolsHubPath(language)} className="hover:text-[#0F1B36]" data-testid="footer-tools">{t('footer.tools')}</Link></li>
                <li><Link to={language === 'en' ? '/en/pricing' : '/pricing'} className="hover:text-[#0F1B36]" data-testid="footer-pricing">{t('footer.pricing')}</Link></li>
                <li><Link to="/e-ihracat-danismanlik" className="hover:text-[#0F1B36]" data-testid="footer-consulting">E-İhracat Danışmanlığı</Link></li>
                <li><Link to="/demo" className="hover:text-[#0F1B36]">{language === 'en' ? 'Sample Report' : 'Örnek Rapor'}</Link></li>
                <li><Link to={language === 'en' ? '/en/blog' : '/blog'} className="hover:text-[#0F1B36]" data-testid="footer-blog">{t('footer.blog')}</Link></li>
              </ul>
            </div>

            <div>
              <p className="font-bold text-[#0F1B36] text-sm mb-3">{t('footer.col.company')}</p>
              <ul className="space-y-2 text-sm text-[#52525B]">
                <li><Link to="/hakkimizda" className="hover:text-[#0F1B36]" data-testid="footer-about">{language === 'en' ? 'About Us' : 'Hakkımızda'}</Link></li>
                <li><Link to="/iletisim" className="hover:text-[#0F1B36]" data-testid="footer-contact">{language === 'en' ? 'Contact' : 'İletişim'}</Link></li>
                <li><a href="mailto:hi@growthriq.net" className="hover:text-[#0F1B36]">hi@growthriq.net</a></li>
              </ul>
            </div>

            <div>
              <p className="font-bold text-[#0F1B36] text-sm mb-3">{t('footer.col.legal')}</p>
              <ul className="space-y-2 text-sm text-[#52525B]">
                <li><Link to="/legal/kvkk" className="hover:text-[#0F1B36]" data-testid="footer-kvkk">{t('footer.kvkk')}</Link></li>
                <li><Link to="/legal/gizlilik" className="hover:text-[#0F1B36]" data-testid="footer-privacy">{language === 'en' ? 'Privacy' : 'Gizlilik'}</Link></li>
                <li><Link to="/legal/terms" className="hover:text-[#0F1B36]" data-testid="footer-terms">{t('footer.terms')}</Link></li>
                <li><Link to="/legal/cookies" className="hover:text-[#0F1B36]" data-testid="footer-cookies">{t('footer.cookies')}</Link></li>
              </ul>
            </div>

            <div>
              <p className="font-bold text-[#0F1B36] text-sm mb-3">{t('footer.col.commerce')}</p>
              <ul className="space-y-2 text-sm text-[#52525B]">
                <li><Link to="/legal/mesafeli-satis" className="hover:text-[#0F1B36]" data-testid="footer-mesafeli">{t('footer.mesafeli')}</Link></li>
                <li><Link to="/legal/on-bilgilendirme" className="hover:text-[#0F1B36]" data-testid="footer-on-bilg">{t('footer.onBilg')}</Link></li>
                <li><Link to="/legal/iade-iptal" className="hover:text-[#0F1B36]" data-testid="footer-refund">{language === 'en' ? 'Refund Policy' : 'İade & İptal'}</Link></li>
              </ul>
            </div>
          </div>

          <div className="pt-6 border-t border-[#E4E4E7] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#52525B]">
            <p>{t('landing.footer')}</p>
            <p className="text-[#52525B]/70">{t('landing.footer.payments')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
